import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Bell, Mail, MessageSquareText, Copy, ChevronDown, ChevronRight, Shield, Send, Loader2 } from "lucide-react";

const EMAIL_LABELS = {
  rent_reminders: { title: "Rent reminders", hint: "Upcoming rent due, late notices." },
  payment_receipts: { title: "Payment receipts", hint: "Confirmations after a rent payment settles." },
  maintenance_updates: { title: "Maintenance updates", hint: "Work order status, vendor dispatch confirmations, photos." },
  monthly_digests: { title: "Monthly reports & digests", hint: "Portfolio valuation, maturity reports, quarterly bundles." },
  marketing: { title: "Product tips & announcements", hint: "Occasional feature announcements and tips. Off by default." },
  dispute_alerts: { title: "Alerts & critical notices", hint: "Chargebacks, loan maturity red-zone, failed payouts." },
};
const SMS_LABELS = {
  rent_reminders: { title: "Rent reminders", hint: "Short SMS 3 days before rent is due." },
  maintenance_emergency: { title: "Maintenance emergencies", hint: "Urgent WO updates only (emergency priority)." },
  payment_confirmations: { title: "Payment confirmations", hint: "One-line SMS when a rent payment settles." },
};

/**
 * Drop-in Settings panel. Handles:
 *   - master email_opt_in + sms_opt_in switches
 *   - per-category toggles (collapsed "Advanced" panel)
 *   - copy-link preview of the user's unsubscribe URL
 *   - optimistic updates + partial PUT payloads
 */
export default function NotificationPreferencesSection({ appOrigin }) {
  const [prefs, setPrefs] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null); // {configured, transport}
  const [smsTestTo, setSmsTestTo] = useState("");
  const [smsTesting, setSmsTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/notification-preferences");
      setPrefs(data.preferences);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load preferences");
    }
  };
  useEffect(() => {
    load();
    api.get("/sms/status").then((r) => setSmsStatus(r.data)).catch(() => setSmsStatus({ configured: false, transport: "mock" }));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const sendTestSms = async () => {
    if (!smsTestTo.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    setSmsTesting(true);
    try {
      const r = await api.post("/sms/test", { to: smsTestTo.trim() });
      const status = r.data?.status || (r.data?.ok ? "sent" : "error");
      if (status === "mock") {
        toast.message("MOCK · would have sent SMS", {
          description: `To ${r.data?.to || smsTestTo.trim()} · set TWILIO_* env vars to enable live sending`,
        });
      } else if (status === "sent") {
        toast.success(`SMS sent to ${r.data?.to || smsTestTo.trim()}`, { description: r.data?.sid ? `Twilio SID ${r.data.sid}` : undefined });
      } else {
        toast.error(r.data?.error || "Send failed");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSmsTesting(false);
    }
  };

  const patch = async (payload, successMsg) => {
    if (!prefs) return;
    // Optimistic merge
    const next = {
      ...prefs,
      ...payload,
      email_categories: { ...prefs.email_categories, ...(payload.email_categories || {}) },
      sms_categories: { ...prefs.sms_categories, ...(payload.sms_categories || {}) },
    };
    setPrefs(next);
    setBusy(true);
    try {
      const { data } = await api.put("/me/notification-preferences", payload);
      setPrefs(data.preferences);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save preferences");
      load();
    } finally { setBusy(false); }
  };

  if (!prefs) return (
    <section className="rounded-sm border border-slate-200 bg-white p-5 animate-pulse">
      <div className="h-5 w-48 bg-slate-200 rounded mb-3" />
      <div className="h-4 w-80 bg-slate-100 rounded" />
    </section>
  );

  const unsubUrl = prefs.unsubscribe_token
    ? `${appOrigin || window.location.origin}/unsubscribe/${prefs.unsubscribe_token}`
    : null;

  const copyUnsub = () => {
    if (!unsubUrl) return;
    navigator.clipboard.writeText(unsubUrl).then(
      () => toast.success("Unsubscribe link copied"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <section className="rounded-sm border border-slate-200 bg-white p-5" data-testid="notif-prefs-section">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-slate-700" />
        <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>
          Notification preferences
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Control which emails and SMS this account receives. Password-reset and security emails are always delivered regardless of these settings.
      </p>

      {/* MASTER TOGGLES */}
      <div className="grid md:grid-cols-2 gap-3" data-testid="notif-prefs-master">
        <MasterToggle
          icon={<Mail className="w-4 h-4" />}
          title="Email notifications"
          subtitle={prefs.email_opt_in
            ? "You're receiving emails based on the categories below."
            : "All email notifications are disabled. You'll only get security + password-reset emails."}
          enabled={prefs.email_opt_in}
          busy={busy}
          onToggle={(v) => patch({ email_opt_in: v }, v ? "Emails re-enabled" : "All emails paused")}
          testid="notif-master-email"
        />
        <MasterToggle
          icon={<MessageSquareText className="w-4 h-4" />}
          title="SMS notifications"
          subtitle={prefs.sms_opt_in
            ? "You can receive text messages where enabled below."
            : "SMS disabled — no text messages will be sent."}
          enabled={prefs.sms_opt_in}
          busy={busy}
          onToggle={(v) => patch({ sms_opt_in: v }, v ? "SMS re-enabled" : "All SMS paused")}
          testid="notif-master-sms"
          extra={smsStatus ? (
            <span
              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${smsStatus.configured ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}
              data-testid="sms-status-badge"
              title={smsStatus.configured ? "Twilio is configured — live SMS will be sent" : "Twilio not configured — set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER in backend env"}
            >
              {smsStatus.configured ? "● Live (Twilio)" : "○ Mock mode"}
            </span>
          ) : null}
        />
      </div>

      {/* SMS test send — verifies Twilio integration & phone formatting */}
      <div className="mt-3 border border-slate-200 rounded-sm p-3 bg-slate-50/50" data-testid="sms-test-row">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquareText className="w-3.5 h-3.5 text-slate-700" />
          <span className="text-xs font-semibold text-slate-700">Send a test SMS</span>
          {smsStatus && !smsStatus.configured ? (
            <span className="text-[10px] text-amber-700 ml-auto" title="Backend env missing TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_FROM_NUMBER">
              Mock mode — no real SMS will be sent
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="tel"
            className="input-clean flex-1 min-w-[180px] font-mono text-xs"
            placeholder="+14155552671 or (415) 555-2671"
            value={smsTestTo}
            onChange={(e) => setSmsTestTo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendTestSms(); } }}
            data-testid="sms-test-to"
          />
          <button
            type="button"
            onClick={sendTestSms}
            disabled={smsTesting || !smsTestTo.trim()}
            className="btn-outline-dark text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="sms-test-send"
          >
            {smsTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {smsTesting ? "Sending…" : "Send test"}
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-slate-500">
          Verifies your Twilio integration & E.164 normalization. The test bypasses notification preferences so you always see a result.
        </p>
      </div>

      {/* ADVANCED */}
      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        className="mt-4 flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-slate-900"
        data-testid="notif-prefs-advanced-toggle"
      >
        {advancedOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        Advanced · per-category preferences
      </button>

      {advancedOpen && (
        <div className="mt-3 border-t border-slate-100 pt-3 grid md:grid-cols-2 gap-4" data-testid="notif-prefs-advanced">
          <CategoryGroup
            label="Email categories"
            icon={<Mail className="w-4 h-4" />}
            disabled={!prefs.email_opt_in}
            disabledHint="Master email switch is off — turn it on to control categories."
            items={Object.entries(prefs.email_categories).map(([k, v]) => ({ key: k, value: v, ...(EMAIL_LABELS[k] || { title: k, hint: "" }) }))}
            onChange={(key, value) => patch({ email_categories: { [key]: value } })}
            testidPrefix="notif-email-cat"
          />
          <CategoryGroup
            label="SMS categories"
            icon={<MessageSquareText className="w-4 h-4" />}
            disabled={!prefs.sms_opt_in}
            disabledHint="Master SMS switch is off."
            items={Object.entries(prefs.sms_categories).map(([k, v]) => ({ key: k, value: v, ...(SMS_LABELS[k] || { title: k, hint: "" }) }))}
            onChange={(key, value) => patch({ sms_categories: { [key]: value } })}
            testidPrefix="notif-sms-cat"
          />
        </div>
      )}

      {/* CAN-SPAM unsubscribe preview */}
      {unsubUrl && (
        <div className="mt-4 border border-dashed border-slate-200 rounded-sm p-3 bg-slate-50 text-xs" data-testid="notif-prefs-unsub-preview">
          <div className="flex items-center gap-1 text-slate-500 overline mb-1">
            <Shield className="w-3 h-3" /> Your personal unsubscribe link
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate text-slate-600 font-mono text-[11px]">{unsubUrl}</code>
            <button
              onClick={copyUnsub}
              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-sm"
              aria-label="Copy unsubscribe link"
              data-testid="notif-prefs-copy-unsub"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="mt-1 text-slate-400 leading-relaxed">
            Every commercial email we send includes a one-click unsubscribe link that points here. You can share it with support if you need help opting out.
          </p>
        </div>
      )}
    </section>
  );
}

function MasterToggle({ icon, title, subtitle, enabled, busy, onToggle, testid, extra }) {
  return (
    <div
      className={`border rounded-sm p-4 flex items-start gap-3 ${enabled ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50"}`}
      data-testid={testid}
    >
      <div className={`w-8 h-8 flex items-center justify-center rounded-sm shrink-0 ${enabled ? "bg-slate-900 text-white" : "bg-slate-300 text-slate-600"}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-slate-900 text-sm" style={{fontFamily:"Outfit"}}>{title}</div>
            {extra}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            disabled={busy}
            onClick={() => onToggle(!enabled)}
            data-testid={`${testid}-switch`}
            className={`relative inline-flex items-center h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 ${enabled ? "bg-emerald-500" : "bg-slate-300"} ${busy ? "opacity-60" : ""}`}
          >
            <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{subtitle}</p>
      </div>
    </div>
  );
}

function CategoryGroup({ label, icon, items, onChange, disabled, disabledHint, testidPrefix }) {
  return (
    <div className={disabled ? "opacity-60" : ""}>
      <div className="flex items-center gap-1 text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
        {icon} {label}
      </div>
      {disabled && (
        <div className="text-[11px] text-slate-400 italic mb-2">{disabledHint}</div>
      )}
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.key} className="flex items-start gap-2" data-testid={`${testidPrefix}-${it.key}`}>
            <input
              type="checkbox"
              id={`${testidPrefix}-${it.key}`}
              checked={!!it.value}
              disabled={disabled}
              onChange={(e) => onChange(it.key, e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
              data-testid={`${testidPrefix}-${it.key}-checkbox`}
            />
            <label htmlFor={`${testidPrefix}-${it.key}`} className="flex-1 min-w-0 cursor-pointer">
              <div className="text-sm font-semibold text-slate-900">{it.title}</div>
              {it.hint && <div className="text-xs text-slate-500 leading-relaxed">{it.hint}</div>}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
